import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, BuildingIcon, UserIcon, CopyIcon, CheckIcon, Trash2Icon, LinkIcon, ClockIcon, UploadIcon } from 'lucide-react';
import { getTenant } from '../../../theme/tokens';
import { Supplier, mockSuppliers, InvitedSupplier, mockInvitedSuppliers } from '../mocks/mockSuppliers';
export function Suppliers() {
  const {
    t
  } = useTranslation('suppliers');
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'owed' | 'invited'>('all');
  const [showArchived, setShowArchived] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [invitedSuppliers, setInvitedSuppliers] = useState<InvitedSupplier[]>(mockInvitedSuppliers);
  const filteredSuppliers = mockSuppliers.filter(supplier => {
    const matchesSearch = supplier.name.toLowerCase().includes(searchQuery.toLowerCase()) || supplier.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = activeTab === 'all' || activeTab === 'owed' && supplier.amountOwed !== `${getTenant().currency.code} 0.00`;
    const matchesArchived = showArchived ? true : !supplier.isArchived;
    return matchesSearch && matchesTab && matchesArchived;
  });
  const filteredInvited = invitedSuppliers.filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const handleCopyInviteLink = (supplier: InvitedSupplier) => {
    const fullLink = `${window.location.origin}${supplier.inviteLink}`;
    navigator.clipboard.writeText(fullLink);
    setCopiedId(supplier.id);
    setTimeout(() => setCopiedId(null), 2000);
  };
  const handleDeleteInvite = (id: string) => {
    setInvitedSuppliers(prev => prev.filter(s => s.id !== id));
  };
  return <div className="space-y-6 max-w-7xl mx-auto" data-id="element-13690">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-13691">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-13692">
          {t('pages.Suppliers.suppliers')}
        </h1>
        <div className="flex items-center gap-3" data-id="element-13693">
          <button onClick={() => navigate('/supplier-bulk-upload')} className="flex items-center px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-13694">
            <UploadIcon size={16} className="mr-2" data-id="element-13695" />
            {t('pages.Suppliers.upload')}
          </button>
          <button onClick={() => navigate('/supplier-form')} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-13696">
            {t('pages.Suppliers.add_supplier')}
          </button>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-13697">
        {/* Controls Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border-b border-gray-200 gap-4" data-id="element-13698">
          {/* Tabs */}
          <div className="flex space-x-6" role="tablist" aria-label="Supplier tabs" data-id="element-13699">
            <button role="tab" aria-selected={activeTab === 'all'} aria-controls="suppliers-tabpanel" id="suppliers-tab-all" onClick={() => setActiveTab('all')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 ${activeTab === 'all' ? 'border-[#E2231A] text-[#9F140F]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-13700">
              {t('pages.Suppliers.all')}
            </button>
            <button role="tab" aria-selected={activeTab === 'owed'} aria-controls="suppliers-tabpanel" id="suppliers-tab-owed" onClick={() => setActiveTab('owed')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 ${activeTab === 'owed' ? 'border-[#E2231A] text-[#9F140F]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-13701">
              {t('pages.Suppliers.money_owed_to')}
            </button>
            <button role="tab" aria-selected={activeTab === 'invited'} aria-controls="suppliers-tabpanel" id="suppliers-tab-invited" onClick={() => setActiveTab('invited')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 ${activeTab === 'invited' ? 'border-[#E2231A] text-[#9F140F]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-13702">
              {t('pages.Suppliers.invited')}
              {invitedSuppliers.length > 0 && <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${activeTab === 'invited' ? 'bg-[#E2231A]/10 text-[#9F140F]' : 'bg-gray-100 text-gray-500'}`} data-id="element-13703">
                  {invitedSuppliers.length}
                </span>}
            </button>
          </div>

          {/* Right Controls */}
          <div className="flex items-center space-x-6" data-id="element-13704">
            {activeTab !== 'invited' && <label className="flex items-center cursor-pointer" data-id="element-13705">
                <span className="text-sm text-gray-600 mr-3 font-medium" data-id="element-13706">
                  {t('pages.Suppliers.show_archived_suppliers')}
                </span>
                <div className="relative" data-id="element-13707">
                  <input type="checkbox" className="sr-only" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} data-id="element-13708" />

                  <div className={`block w-10 h-6 rounded-full transition-colors ${showArchived ? 'bg-[#E2231A]' : 'bg-gray-200'}`} data-id="element-13709"></div>
                  <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${showArchived ? 'transform translate-x-4' : ''}`} data-id="element-13710"></div>
                </div>
              </label>}

            <div className="relative w-64" data-id="element-13711">
              <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-13712" />

              <input type="search" aria-label={t('pages.Suppliers.attrs.search_by_supplier')} placeholder={t('pages.Suppliers.attrs.search_by_supplier')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-13713" />
            </div>
          </div>
        </div>

        {/* Active Suppliers Table */}
        {activeTab !== 'invited' && <div className="overflow-x-auto" data-id="element-13714">
            <table className="w-full text-left border-collapse" data-id="element-13715">
              <thead data-id="element-13716">
                <tr className="border-b border-gray-100" data-id="element-13717">
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13718">
                    {t('pages.Suppliers.supplier_detail')}
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13719">
                    {t('pages.BulkSupplierUpload.type')}
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13720">
                    {t('pages.Suppliers.you_owe_them')}
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13721">
                    {t('pages.SupplierDetail.source')}
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-13722">
                    {t('pages.SupplierDetail.date_created')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-13723">
                {filteredSuppliers.map(supplier => <tr key={supplier.id} onClick={() => navigate(`/supplier-detail/${supplier.id}`)} className="hover:bg-gray-50 cursor-pointer transition-colors group" data-id="element-13724">
                    <td className="px-6 py-4" data-id="element-13725">
                      <div className="text-sm font-medium text-gray-900 group-hover:text-[#9F140F] transition-colors" data-id="element-13726">
                        {supplier.name}
                      </div>
                      <div className="text-sm text-gray-500 mt-0.5" data-id="element-13727">
                        {supplier.email}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-13728">
                      {supplier.supplierType === 'company' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100" data-id="element-13729">
                          <BuildingIcon size={12} className="mr-1.5" data-id="element-13730" />
                          {t('pages.SupplierDetail.company')}
                        </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100" data-id="element-13731">
                          <UserIcon size={12} className="mr-1.5" data-id="element-13732" />
                          {t('pages.SupplierDetail.individual')}
                        </span>}
                    </td>
                    <td className="px-6 py-4" data-id="element-13733">
                      <div className="text-sm font-medium text-gray-900" data-id="element-13734">
                        {supplier.amountOwed}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-13735">
                      <div className="text-sm text-gray-500" data-id="element-13736">
                        {supplier.source}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-13737">
                      <div className="text-sm text-gray-500" data-id="element-13738">
                        {supplier.dateCreated}
                      </div>
                    </td>
                  </tr>)}
                {filteredSuppliers.length === 0 && <tr data-id="element-13739">
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500 text-sm" data-id="element-13740">
                      {t('pages.Suppliers.no_suppliers_found_matching_your_criteria')}
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>}

        {/* Invited Suppliers Table */}
        {activeTab === 'invited' && <div className="overflow-x-auto" data-id="element-13741">
            <table className="w-full text-left border-collapse" data-id="element-13742">
              <thead data-id="element-13743">
                <tr className="border-b border-gray-100" data-id="element-13744">
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13745">
                    {t('pages.Suppliers.supplier_name')}
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13746">
                    {t('pages.BulkSupplierUpload.type')}
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13747">
                    {t('pages.Suppliers.status')}
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13748">
                    {t('pages.Suppliers.invited')}
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-13749">
                    {t('pages.Suppliers.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-13750">
                {filteredInvited.map(supplier => <tr key={supplier.id} className="group" data-id="element-13751">
                    <td className="px-6 py-4" data-id="element-13752">
                      <div className="text-sm font-medium text-gray-900" data-id="element-13753">
                        {supplier.name}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-13754">
                      {supplier.supplierType === 'company' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100" data-id="element-13755">
                          <BuildingIcon size={12} className="mr-1.5" data-id="element-13756" />
                          {t('pages.SupplierDetail.company')}
                        </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100" data-id="element-13757">
                          <UserIcon size={12} className="mr-1.5" data-id="element-13758" />
                          {t('pages.SupplierDetail.individual')}
                        </span>}
                    </td>
                    <td className="px-6 py-4" data-id="element-13759">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200" data-id="element-13760">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" data-id="element-13761"></span>
                        {t('pages.Suppliers.pending')}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-13762">
                      <div className="text-sm text-gray-900" data-id="element-13763">
                        {supplier.dateInvited}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1" data-id="element-13764">
                        <ClockIcon size={11} data-id="element-13765" />
                        {supplier.daysAgo} {t('pages.Suppliers.days_ago')}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-13766">
                      <div className="flex items-center justify-end gap-2" data-id="element-13767">
                        <button onClick={() => handleCopyInviteLink(supplier)} className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-13768">
                          {copiedId === supplier.id ? <>
                              <CheckIcon size={13} className="mr-1.5 text-green-600" data-id="element-13769" />

                              {t('pages.SupplierForm.copied')}
                            </> : <>
                              <CopyIcon size={13} className="mr-1.5" data-id="element-13770" />
                              {t('pages.Suppliers.copy_link')}
                            </>}
                        </button>
                        <button onClick={() => handleDeleteInvite(supplier.id)} className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-600 bg-white border border-gray-200 rounded-lg hover:bg-red-50 hover:border-red-200 transition-colors" data-id="element-13771">
                          <Trash2Icon size={13} className="mr-1.5" data-id="element-13772" />
                          {t('pages.Suppliers.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>)}
                {filteredInvited.length === 0 && <tr data-id="element-13773">
                    <td colSpan={5} className="px-6 py-12 text-center text-sm" data-id="element-13774">
                      <div className="flex flex-col items-center gap-3" data-id="element-13775">
                        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-13776">
                          <LinkIcon size={20} className="text-gray-400" data-id="element-13777" />
                        </div>
                        <div data-id="element-13778">
                          <p className="text-gray-700 font-medium" data-id="element-13779">
                            {t('pages.Suppliers.no_pending_invitations')}
                          </p>
                          <p className="text-gray-500 mt-0.5" data-id="element-13780">
                            {t('pages.Suppliers.invite_a_supplier_by_clicking_add')}
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>}
      </div>
    </div>;
}